import React, { useEffect, useState } from "react";
import {
  Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup,
  Alert, Spinner, Badge,
} from "reactstrap";
import { getMyAccessRequest, submitAccessRequest } from "../../services/accessRequestService";
import { getLoggedinUser } from "../../helpers/api_helper";
import ResearchDisclaimer from "../../Components/Common/ResearchDisclaimer";

/**
 * "Request early access" — what stands in for a checkout while billing is off.
 *
 * A Subscribe button that opens a checkout which cannot complete is worse than
 * no button. This asks instead, which is the thing actually worth collecting
 * right now: permission to write to someone, and which tier they wanted.
 *
 * Shows its own state. Someone who has already asked should see that they have,
 * not an unchanged button that leaves them wondering whether the click landed.
 */

const STATUS_LABEL = {
  pending: { color: "warning", text: "Request received" },
  contacted: { color: "info", text: "We have been in touch" },
  granted: { color: "success", text: "Access granted" },
  declined: { color: "light", text: "Request closed" },
};

const RequestAccessButton = ({
  color = "primary",
  className = "",
  block = false,
  label = "Request early access",
  defaultTier = null,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [request, setRequest] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [tier, setTier] = useState(defaultTier ? String(defaultTier) : "");
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);

  const signedIn = !!(getLoggedinUser() || {}).token;

  useEffect(() => {
    if (!signedIn) { setLoading(false); return; }
    let live = true;
    (async () => {
      try {
        const res = await getMyAccessRequest();
        if (!live) return;
        setRequest(res.data?.request || null);
        setTiers(res.data?.tiers || []);
        if (res.data?.request?.requested_tier) {
          setTier(String(res.data.request.requested_tier));
        }
        if (res.data?.request?.message) setMessage(res.data.request.message);
      } catch (e) {
        // Not being able to read an existing request is not worth an error in
        // the member's face — the form still works.
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [signedIn]);

  const onOpen = () => {
    // Asking requires an account, because the whole point is being able to
    // reply. Send them to sign in and bring them back here.
    if (!signedIn) {
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    setError(null);
    setOpen(true);
  };

  const onSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await submitAccessRequest({
        requestedTier: tier ? Number(tier) : null,
        message,
      });
      setRequest(res.data);
      setOpen(false);
    } catch (e) {
      setError(e?.error || e?.message || "Could not send that request.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const status = request ? STATUS_LABEL[request.status] : null;

  return (
    <>
      <div className={block ? "w-100" : ""}>
        <Button color={request ? "light" : color}
                className={`${block ? "w-100" : ""} ${className}`}
                onClick={onOpen}>
          {request ? "Update my request" : label}
        </Button>
        {status && (
          <div className="mt-2">
            <Badge color={status.color}>{status.text}</Badge>
            {request.status === "pending" && (
              <span className="text-muted fs-12 ms-2">
                We will email you at the address on your account.
              </span>
            )}
          </div>
        )}
      </div>

      <Modal isOpen={open} toggle={() => setOpen(false)} centered>
        <ModalHeader toggle={() => setOpen(false)}>
          {request ? "Update your request" : "Request early access"}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted fs-13">
            Paid plans are not on sale yet. Ask here and we will get in touch — asking
            also puts you in the group we open the paid features to first.
          </p>

          <FormGroup>
            <Label for="req-tier" className="fs-13">What are you after?</Label>
            <Input type="select" id="req-tier" value={tier}
                   onChange={(e) => setTier(e.target.value)}>
              <option value="">Not sure yet — just want more than the free tier</option>
              {tiers.map((t) => (
                <option key={t.tier} value={t.tier}>
                  {t.name} — ${t.price_usd}/mo
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="req-message" className="fs-13">
              Anything you want us to know? <span className="text-muted">(optional)</span>
            </Label>
            <Input type="textarea" id="req-message" rows={4} value={message}
                   placeholder="Which symbols you follow, what you would use it for, what is missing…"
                   onChange={(e) => setMessage(e.target.value)} />
            <p className="text-muted fs-12 mt-1 mb-0">
              What you say here is the most useful part — it tells us what to build next.
            </p>
          </FormGroup>

          {error && <Alert color="danger" className="fs-13 mb-3">{error}</Alert>}

          <div className="border-top pt-3">
            <ResearchDisclaimer />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={onSubmit} disabled={saving}>
            {saving ? <><Spinner size="sm" /> Sending…</> : request ? "Update request" : "Send request"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default RequestAccessButton;
